import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, updateDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import LoadingAnimation from './LoadingAnimation';

const AdminPrayerRequests = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login', { state: { from: location.pathname } });
      } else if (config.configLoaded && config.adminEmails.length > 0 && !config.adminEmails.includes(user.email)) {
        navigate('/');
      } else if (config.configLoaded) {
        fetchRequests().then(() => setLoading(false));
      }
    });
    return () => unsubscribe();
  }, [config.configLoaded, config.adminEmails]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRequests = async () => {
    const snap = await getDocs(query(collection(db, 'prayer_requests'), orderBy('created_at', 'desc')));
    setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const markPrayedFor = async (id) => {
    await updateDoc(doc(db, 'prayer_requests', id), { status: 'prayed_for' });
    await fetchRequests();
  };

  const markNew = async (id) => {
    await updateDoc(doc(db, 'prayer_requests', id), { status: 'new' });
    await fetchRequests();
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  if (loading) return <div style={{ minHeight: '100vh' }}><LoadingAnimation /></div>;

  return (
    <div className="stt-admin-page">
      <a href="/admin" className="stt-admin-back">← Back to Admin</a>
      <h1 className="stt-admin-title">Prayer Requests</h1>
      <p style={{ color: '#666', fontSize: '0.88rem', marginBottom: '4px' }}>
        {requests.filter(r => r.status === 'new').length} new · {requests.length} total
      </p>
      <hr />

      {/* Filter */}
      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        {['all', 'new', 'prayed_for'].map(f => (
          <button
            key={f}
            className={`btn btn-sm ${filter === f ? 'stt-btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : f === 'new' ? 'New' : 'Prayed For'}
          </button>
        ))}
      </div>

      <div className="stt-admin-card">
        {filtered.length === 0 ? (
          <p style={{ color: '#888' }}>No requests in this category.</p>
        ) : (
          filtered.map(req => (
            <div key={req.id} style={{
              padding: '16px 0',
              borderBottom: '1px solid #eee',
              opacity: req.status === 'prayed_for' ? 0.65 : 1
            }}>
              <div style={{ marginBottom: '6px' }}>
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{req.name || 'Anonymous'}</span>
                <span style={{ fontSize: '0.78rem', color: '#888', marginLeft: '10px' }}>{formatDate(req.created_at)}</span>
              </div>
              <p style={{ fontSize: '0.92rem', color: '#333', margin: '0 0 12px', lineHeight: 1.6 }}>{req.message}</p>
              {req.status !== 'prayed_for' ? (
                <button className="btn btn-sm stt-btn-primary" onClick={() => markPrayedFor(req.id)}>
                  Mark Prayed For
                </button>
              ) : (
                <button className="btn btn-sm btn-outline-secondary" onClick={() => markNew(req.id)}>
                  Mark as New
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminPrayerRequests;
